'use client';

import { FetchWithGuardRequestInit, useClientConfig } from '@spikemark/core';
import { useDeepMemo } from '../use-deep-memo';
import { useFetcher } from './use-fetcher';
import { UseRemoteHookResult, UseRemoteOptions, useRemote } from './use-remote';

export type UseFetchHookResult<TData> = UseRemoteHookResult<TData>;

export type UseFetchOptions<TData, TParams = undefined> = UseRemoteOptions<TData, TParams>;

export type UseFetchRequestInit = FetchWithGuardRequestInit;

export function useFetch<TResult>(
  path: string,
  method?: RequestInit['method'],
  options?: UseFetchOptions<TResult>,
  init?: UseFetchRequestInit
): UseFetchHookResult<TResult>;

export function useFetch<TResult, TParams>(
  path: string,
  method?: RequestInit['method'],
  options?: UseFetchOptions<TResult, TParams>,
  init?: UseFetchRequestInit
): UseFetchHookResult<TResult>;

export function useFetch<TResult, TParams = undefined>(
  path: string,
  method: RequestInit['method'] = 'GET',
  options?: UseFetchOptions<TResult, TParams>,
  init?: UseFetchRequestInit
): UseFetchHookResult<TResult> {
  const { cacheRevalidate, disableCache, fetchRequestMaxRetries, fetchRequestTimeout } =
    useClientConfig();

  const key = `${path}${options?.params ? `:${JSON.stringify(options.params)}` : ''}`;

  options = useDeepMemo({
    ...options,
    key,
  });

  init = useDeepMemo({
    ...init,
    cache: disableCache ? 'no-cache' : 'default',
    next: {
      revalidate: disableCache ? 0 : cacheRevalidate,
    },
    retries: fetchRequestMaxRetries,
    timeout: fetchRequestTimeout,
  });

  const fetcher = useFetcher<TResult, TParams>(path, method, init);

  return useRemote<TResult, TParams>(fetcher, options);
}
